import { useRef, useState } from "react";
import { Button } from "./Button";
import { Input } from "./Input";
import { Modal } from "./Modal";
import { ModalContent, ModalFooter } from "./ModalCard";
import { Text } from "./Text";

type Props = {
  title: string;
  inputLabel: string;
  initialName: string;
  maxLength?: number;
  validateName: (name: string) => string | null;
  confirmLabel?: string;
  onClose: () => void;
  onRename: (name: string) => void;
};

export function RenameModal({
  title,
  inputLabel,
  initialName,
  maxLength,
  validateName,
  confirmLabel = "Save",
  onClose,
  onRename,
}: Props) {
  const [name, setName] = useState(initialName);
  const inputRef = useRef<HTMLInputElement>(null);
  const trimmed = name.trim();
  const error = validateName(trimmed);

  const submit = () => {
    if (error) return;
    if (trimmed !== initialName) onRename(trimmed);
    onClose();
  };

  return (
    <Modal open onClose={onClose} alert initialFocusRef={inputRef}>
      <ModalContent>
        <Text variant="text-regular-md" color="var(--text-primary)">
          {title}
        </Text>
        <Input
          ref={inputRef}
          aria-label={inputLabel}
          value={name}
          maxLength={maxLength}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.nativeEvent.isComposing) {
              e.preventDefault();
              submit();
            }
          }}
        />
        {trimmed && error && (
          <Text variant="text-small" color="var(--red-600)">
            {error}
          </Text>
        )}
        <ModalFooter>
          <Button size="l" variant="subtle" onClick={onClose}>
            Cancel
          </Button>
          <Button size="l" disabled={error !== null} onClick={submit}>
            {confirmLabel}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
